/**
 * SportsFitnessServices - Delivery service stack for Sports, Fitness & Hobbies
 * listings. Single panel with 3 courier partners tuned for the spectrum:
 *   - White-glove 2-person for treadmills / exercise bikes / rowing machines
 *   - Bike & cycling specialist courier
 *   - Standard tracked for small kit (yoga mats, dumbbells, camping gear)
 *
 * Every card carries a DEMO badge until a real courier signs up. The stack
 * respects the existing demo-visibility admin CMS (per-category rule on
 * sports-fitness-hobbies + global kill switch).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Truck, Phone, MapPin, Clock, Star, AlertCircle, ShieldCheck, Bike } from 'lucide-react';

const DELIVERY_DEMOS_UK = [
  {
    id: 'sf-del-1', name: 'Panther Big & Bulky',
    type: 'White-Glove 2-Person',
    tagline: 'Treadmills, rowing machines, home-gym rigs - up 3 flights of stairs',
    rating: 4.7, reviews: 3210, phone: '0330 002 0800',
    coverage: 'UK mainland, 2-hr slot booking',
    tags: ['Room of Choice', 'Assembly', 'Old Item Removal'],
    price_from: 55, eta: 'Next-day / 2-day',
    icon: Truck,
  },
  {
    id: 'sf-del-2', name: 'BikeCare Direct',
    type: 'Bike & Cycling Specialist',
    tagline: 'Fully-boxed bike shipping with track-day protection wrap',
    rating: 4.9, reviews: 1840, phone: '0800 049 6220',
    coverage: 'UK & EU, 48hr express',
    tags: ['Bike-only', 'Anti-scratch Wrap', '£1000 insurance'],
    price_from: 42, eta: '48 hours UK / 5 days EU',
    icon: Bike,
  },
  {
    id: 'sf-del-3', name: 'DPD Local - Standard Sport',
    type: 'Standard Courier',
    tagline: 'Yoga mats, dumbbells, camping gear, small kit (up to 30kg)',
    rating: 4.8, reviews: 5240, phone: '0121 275 0500',
    coverage: 'UK-wide, next-day',
    tags: ['Next-Day', '1-hr Slot', 'Photo Proof'],
    price_from: 7.5, eta: 'Next-day',
    icon: Truck,
  },
];

const DELIVERY_DEMOS_SL = [
  {
    id: 'sf-del-1-sl', name: 'Kapruka Big & Bulky',
    type: 'White-Glove 2-Person',
    tagline: 'Treadmills, rowing machines, home-gym rigs - brought upstairs',
    rating: 4.6, reviews: 2140, phone: '011 483 6436',
    coverage: 'Colombo + suburbs, 2-hr slot',
    tags: ['Room of Choice', 'Assembly', 'Old Item Removal'],
    price_from: 6500, eta: 'Next-day / 2-day',
    icon: Truck,
  },
  {
    id: 'sf-del-2-sl', name: 'CycleSafe Lanka',
    type: 'Bike & Cycling Specialist',
    tagline: 'Fully-boxed bike shipping with protection wrap',
    rating: 4.8, reviews: 620, phone: '077 331 7788',
    coverage: 'Islandwide, 48hr express',
    tags: ['Bike-only', 'Anti-scratch Wrap', 'Rs 25,000 insurance'],
    price_from: 3500, eta: '48 hours',
    icon: Bike,
  },
  {
    id: 'sf-del-3-sl', name: 'Pronto Lanka - Sport & Camping',
    type: 'Standard Courier',
    tagline: 'Yoga mats, dumbbells, camping gear, small kit (up to 30kg)',
    rating: 4.6, reviews: 1820, phone: '011 231 8888',
    coverage: 'Islandwide, next-day',
    tags: ['Next-Day', 'Cash on Delivery', 'Photo Proof'],
    price_from: 650, eta: 'Next-day',
    icon: Truck,
  },
];

const ProviderCard = ({ provider, isDemo, currencyPrefix = '£' }) => (
  <div
    className="relative border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
    data-testid={`sf-delivery-provider-${provider.id}`}
  >
    {isDemo && (
      <div
        className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl"
        data-testid={`sf-delivery-provider-demo-${provider.id}`}
      >
        DEMO
      </div>
    )}
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-orange-50 border-orange-200 text-orange-600">
        <provider.icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h4 className="font-semibold text-gray-900 text-sm">{provider.name}</h4>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{provider.type}</Badge>
        </div>
        <p className="text-xs text-gray-500 italic mt-0.5">{provider.tagline}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <span className="flex items-center gap-0.5 text-amber-600">
            <Star className="h-3 w-3 fill-current" />
            {provider.rating.toFixed(1)}
          </span>
          <span>({provider.reviews.toLocaleString()} reviews)</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-gray-600">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{provider.coverage}</span>
          <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{provider.eta}</span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {provider.tags.map((tag) => (
            <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tag}</span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold text-emerald-700">From {currencyPrefix}{provider.price_from.toLocaleString()}</span>
          <Button
            size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => window.open(`tel:${provider.phone.replace(/\s/g, '')}`, '_self')}
            data-testid={`sf-delivery-provider-call-${provider.id}`}
          >
            <Phone className="h-3 w-3 mr-1" /> Call
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const SportsFitnessServices = ({ listing }) => {
  const isDemo = !!listing?.is_demo;
  const country = (listing?.country || '').toUpperCase();
  const isSL = country === 'LK' || country === 'SL';
  const deliveryDemos = isSL ? DELIVERY_DEMOS_SL : DELIVERY_DEMOS_UK;
  const currencyPrefix = isSL ? 'Rs. ' : '£';

  return (
    <div className="space-y-6" data-testid="sports-fitness-services-stack">
      {isDemo && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="sports-fitness-services-demo-notice">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <Badge variant="secondary" className="mr-2 bg-amber-100 text-amber-800 border-amber-200">DEMO</Badge>
            Courier options below are illustrative for this demo listing. Live couriers appear on real seller listings.
          </AlertDescription>
        </Alert>
      )}

      <Card data-testid="sports-fitness-delivery-section">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Truck className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-lg">Delivery & Setup</CardTitle>
            <Badge variant="outline" className="text-xs">Courier partners</Badge>
            {isDemo && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="sf-delivery-demo-badge">DEMO</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            From treadmills to yoga mats - pick the right courier for {listing?.title || 'your item'}.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {deliveryDemos.map((p) => (
              <ProviderCard key={p.id} provider={p} isDemo={true} currencyPrefix={currencyPrefix} />
            ))}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
            <span>Every partner carries goods-in-transit insurance - damage during delivery is covered.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SportsFitnessServices;
